<template>
  <div class="container-fluid py-4">
    <h2 class="text-2xl font-bold mb-4">
      <i class="bx bx-money"></i> Historial de Pagos Hospedaje
    </h2>

    <!-- Filtros -->
    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-2">
            <label class="form-label">Fecha Inicio</label>
            <input type="date" v-model="filtros.fechaInicio" class="form-control" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Hora Inicio</label>
            <input type="time" v-model="filtros.horaInicio" class="form-control" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Fecha Fin</label>
            <input type="date" v-model="filtros.fechaFin" class="form-control" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Hora Fin</label>
            <input type="time" v-model="filtros.horaFin" class="form-control" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Método de Pago</label>
            <select v-model="filtros.idMetodoPago" class="form-select">
              <option :value="null">— Todos —</option>
              <option v-for="m in metodosPago" :key="m.IdMetodoPago" :value="m.IdMetodoPago">
                {{ m.Metodo }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label">Ordenar por</label>
            <select v-model="filtros.ordenar" class="form-select">
              <option value="fecha_desc">Fecha (más reciente)</option>
              <option value="fecha_asc">Fecha (más antigua)</option>
              <option value="monto_desc">Monto (mayor)</option>
              <option value="monto_asc">Monto (menor)</option>
              <option value="metodo_asc">Método (A-Z)</option>
            </select>
          </div>
          <div class="col-md-2 d-flex align-items-end gap-1">
            <button @click="cargarPagos" class="btn btn-primary">
              <i class="bx bx-search"></i> Buscar
            </button>
            <button @click="limpiarFiltros" class="btn btn-secondary">
              <i class="bx bx-x"></i>
            </button>
          </div>
        </div>

        <!-- Botones de exportación -->
        <div class="mt-3 d-flex gap-2">
          <button @click="descargarPDF" class="btn btn-outline-danger btn-sm" :disabled="!pagos.length">
            <i class="bx bxs-file-pdf"></i> Exportar PDF
          </button>
          <button @click="descargarExcel" class="btn btn-outline-success btn-sm" :disabled="!pagos.length">
            <i class="bx bxs-file-export"></i> Exportar Excel
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Tabla de pagos -->
    <div v-else class="card shadow-sm">
      <div class="card-body">
        <!-- Resumen -->
        <div class="alert alert-info mb-3" v-if="pagos.length">
          <strong>Total cobrado:</strong>
          Q{{ totalCobrado.toFixed(2) }}
          &nbsp;|&nbsp;
          <strong>Pagos activos:</strong> {{ pagosActivos }}
          &nbsp;|&nbsp;
          <strong>Pagos anulados:</strong> {{ pagosAnulados }}
        </div>

        <div class="table-responsive">
          <table class="table table-hover">
            <thead class="table-light">
              <tr>
                <th>Fecha y Hora</th>
                <th>Reserva</th>
                <th>Hab.</th>
                <th>Método</th>
                <th>Monto</th>
                <th>Referencia</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="pagos.length === 0">
                <td colspan="8" class="text-center text-muted py-4">
                  No hay pagos registrados con los filtros seleccionados
                </td>
              </tr>
              <tr
                v-for="pago in pagosOrdenados"
                :key="pago.IdPagoHospedajeHotel"
                :class="pago.Anulado === 1 ? 'table-secondary text-muted' : ''"
              >
                <td>{{ formatearFechaHora(pago.FechaHora) }}</td>
                <td>
                  <span v-if="pago.reserva">
                    <router-link
                      :to="`/pagar-por-habitacion/${pago.habitacion?.IdReservaHabitacion}`"
                      class="text-decoration-none"
                    >
                      #{{ pago.reserva.IdReserva }}
                    </router-link>
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <strong v-if="pago.habitacion">
                    {{ pago.habitacion.NumeroHabitacion }}
                  </strong>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  {{ pago.metodo_pago?.Metodo || 'N/A' }}
                  <small v-if="pago.cuenta_bancaria" class="d-block text-muted">
                    {{ pago.cuenta_bancaria.NombreCuenta }} - {{ pago.cuenta_bancaria.Banco }}
                  </small>
                </td>
                <td>
                  <strong>Q{{ parseFloat(pago.Monto).toFixed(2) }}</strong>
                </td>
                <td>{{ pago.NumeroReferencia || pago.NumeroFacturaManual || '—' }}</td>
                <td>
                  <span :class="pago.Anulado === 1 ? 'badge bg-danger' : 'badge bg-success'">
                    {{ pago.Anulado === 1 ? 'Anulado' : 'Activo' }}
                  </span>
                </td>
                <td>
                  <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                    <button
                      class="btn btn-outline-warning btn-sm"
                      @click="imprimirFacturaCarta(pago.factura_fel.uuid)"
                      title="Imprimir factura en hoja carta"
                    >📝 Factura</button>
                    <button
                      class="btn btn-outline-warning btn-sm"
                      @click="descargarFacturaCarta(pago.factura_fel.uuid)"
                      title="Descargar factura carta (PDF)"
                    ><i class="bx bxs-download"></i></button>
                  </div>
                  <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="imprimirTicketTermica(pago.factura_fel.uuid)"
                      title="Imprimir ticket térmico 80mm"
                    >🧾 Térmica</button>
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      @click="descargarTicketTermica(pago.factura_fel.uuid)"
                      title="Descargar ticket térmico (PDF)"
                    ><i class="bx bxs-download"></i></button>
                  </div>
                  <div v-if="pago.factura_fel?.uuid" class="btn-group me-1">
                    <button
                      class="btn btn-outline-success btn-sm"
                      @click="imprimirPdfFel(pago.factura_fel.uuid)"
                      title="Reimprimir factura electrónica (TotalDoc)"
                    >📄 Cert.</button>
                    <button
                      class="btn btn-outline-success btn-sm"
                      @click="descargarPdfFel(pago.factura_fel.uuid)"
                      title="Descargar PDF del certificador"
                    ><i class="bx bxs-download"></i></button>
                  </div>
                  <button
                    class="btn btn-outline-primary btn-sm me-1"
                    @click="imprimirRecibo(pago)"
                    title="Reimprimir recibo"
                  >
                    🖨️ Recibo
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-3 text-center text-muted small">
          Mostrando {{ pagosOrdenados.length }} pagos
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/config/axios';
import { API_URL } from '@/config/config';
import { BRAND } from '@/config/branding';

export default {
  name: 'HistorialPagosHotel',
  data() {
    return {
      pagos: [],
      loading: false,
      metodosPago: [],
      filtros: {
        fechaInicio: '',
        horaInicio: '00:00',
        fechaFin: '',
        horaFin: '23:59',
        idMetodoPago: null,
        ordenar: 'fecha_desc',
      },
    };
  },
  computed: {
    pagosOrdenados() {
      const arr = [...this.pagos];
      switch (this.filtros.ordenar) {
        case 'fecha_asc':  return arr.sort((a, b) => new Date(a.FechaHora) - new Date(b.FechaHora));
        case 'monto_desc': return arr.sort((a, b) => parseFloat(b.Monto) - parseFloat(a.Monto));
        case 'monto_asc':  return arr.sort((a, b) => parseFloat(a.Monto) - parseFloat(b.Monto));
        case 'metodo_asc': return arr.sort((a, b) => (a.metodo_pago?.Metodo || '').localeCompare(b.metodo_pago?.Metodo || ''));
        default:           return arr.sort((a, b) => new Date(b.FechaHora) - new Date(a.FechaHora));
      }
    },
    pagosActivos() {
      return this.pagos.filter(p => p.Anulado === 0).length;
    },
    pagosAnulados() {
      return this.pagos.filter(p => p.Anulado === 1).length;
    },
    totalCobrado() {
      return this.pagos
        .filter(p => p.Anulado === 0)
        .reduce((sum, p) => sum + parseFloat(p.Monto || 0), 0);
    },
  },
  async mounted() {
    this.inicializarFechas();
    await Promise.all([this.cargarMetodosPago(), this.cargarPagos()]);
  },
  methods: {
    inicializarFechas() {
      const hoy = new Date();
      this.filtros.fechaInicio = hoy.toISOString().split('T')[0];
      this.filtros.fechaFin = hoy.toISOString().split('T')[0];
    },
    async cargarMetodosPago() {
      try {
        const { data } = await axios.get(`${API_URL}/metodos-pago/facturados`);
        this.metodosPago = data.data || data || [];
      } catch (e) {
        console.error('Error cargando métodos de pago:', e);
      }
    },
    async cargarPagos() {
      this.loading = true;
      try {
        const params = {
          fecha_inicio: this.filtros.fechaInicio,
          hora_inicio: this.filtros.horaInicio,
          fecha_fin: this.filtros.fechaFin,
          hora_fin: this.filtros.horaFin,
        };
        if (this.filtros.idMetodoPago) params.id_metodo_pago = this.filtros.idMetodoPago;
        const { data } = await axios.get(`${API_URL}/historial-pagos-hotel`, { params });
        this.pagos = data.pagos || [];
      } catch (error) {
        console.error('Error cargando historial de pagos hotel:', error);
      } finally {
        this.loading = false;
      }
    },
    limpiarFiltros() {
      this.inicializarFechas();
      this.filtros.horaInicio = '00:00';
      this.filtros.horaFin = '23:59';
      this.filtros.idMetodoPago = null;
      this.cargarPagos();
    },
    buildParams() {
      const p = {
        fecha_inicio: this.filtros.fechaInicio,
        hora_inicio: this.filtros.horaInicio,
        fecha_fin: this.filtros.fechaFin,
        hora_fin: this.filtros.horaFin,
        ordenar: this.filtros.ordenar,
      };
      if (this.filtros.idMetodoPago) p.id_metodo_pago = this.filtros.idMetodoPago;
      return p;
    },
    async descargarPDF() {
      try {
        const response = await axios.get(`${API_URL}/historial-pagos-hotel-pdf`, {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        link.download = `historial-pagos-hotel-${this.filtros.fechaInicio}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error(e);
        alert('Error al generar el PDF.');
      }
    },
    async descargarExcel() {
      try {
        const response = await axios.get(`${API_URL}/historial-pagos-hotel-excel`, {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));
        link.download = `historial-pagos-hotel-${this.filtros.fechaInicio}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error(e);
        alert('Error al exportar el Excel.');
      }
    },
    formatearFechaHora(fechaHora) {
      if (!fechaHora) return '—';
      const fecha = new Date(fechaHora);
      return fecha.toLocaleString('es-GT', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    },
    descargarFacturaCarta(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/carta`, { responseType: 'blob' }).then(res => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = `factura-${uuid}.pdf`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => {});
    },
    descargarTicketTermica(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/ticket`, { responseType: 'blob' }).then(res => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = `ticket-${uuid}.pdf`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => {});
    },
    descargarPdfFel(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/pdf`, { responseType: 'blob' }).then(res => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = `certificador-${uuid}.pdf`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => {});
    },
    imprimirRecibo(pago) {
      const contenido = `
        <html><head><title>Recibo</title>
        <style>body{font-family:monospace;font-size:12px;padding:10px;}</style>
        </head><body>
        <h3 style="text-align:center">${BRAND.nombre.toUpperCase()}</h3>
        <p style="text-align:center">Comprobante de Pago</p>
        <hr>
        <p>Fecha: ${this.formatearFechaHora(pago.FechaHora)}</p>
        <p>Reserva: ${pago.reserva?.IdReserva || '—'}</p>
        <p>Habitación: ${pago.habitacion?.NumeroHabitacion || '—'}</p>
        <p>Método: ${pago.metodo_pago?.Metodo || 'N/A'}</p>
        ${pago.NumeroReferencia ? `<p>Referencia: ${pago.NumeroReferencia}</p>` : ''}
        <hr>
        <p><strong>Monto: Q${parseFloat(pago.Monto).toFixed(2)}</strong></p>
        <hr>
        <p style="text-align:center">Gracias por su visita</p>
        </body></html>
      `;
      const ventana = window.open('', '_blank', 'width=300,height=400');
      if (ventana) {
        ventana.document.write(contenido);
        ventana.document.close();
        ventana.print();
      }
    },
    imprimirFacturaCarta(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/carta`, { responseType: 'blob' }).then(res => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const existente = document.getElementById('hph-carta-frame');
        if (existente) existente.remove();
        const iframe = document.createElement('iframe');
        iframe.id = 'hph-carta-frame';
        iframe.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;border:none;';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => { try { iframe.contentWindow.print(); } catch (e) { window.open(blobUrl, '_blank'); } };
      }).catch(() => {});
    },
    imprimirTicketTermica(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/ticket`, { responseType: 'blob' }).then(res => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const existente = document.getElementById('hph-ticket-frame');
        if (existente) existente.remove();
        const iframe = document.createElement('iframe');
        iframe.id = 'hph-ticket-frame';
        iframe.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;border:none;';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => { try { iframe.contentWindow.print(); } catch (e) { window.open(blobUrl, '_blank'); } };
      }).catch(() => {});
    },
    imprimirPdfFel(uuid) {
      axios.get(`${API_URL}/facturas-fel/${uuid}/pdf`, { responseType: 'blob' }).then(res => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const existente = document.getElementById('hph-pdf-frame');
        if (existente) existente.remove();
        const iframe = document.createElement('iframe');
        iframe.id = 'hph-pdf-frame';
        iframe.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;border:none;';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => { try { iframe.contentWindow.print(); } catch (e) { window.open(blobUrl, '_blank'); } };
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}
</style>
